import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './ShelfPulse.css';

function ShelfPulseTerms() {
  return (
    <div className="sp-page">
      <Helmet>
        <title>Terms of Service - ShelfPulse</title>
        <meta name="description" content="Terms of Service for ShelfPulse, your audiobook dashboard for Audiobookshelf servers." />
        <link rel="canonical" href="https://audrabooks.app/shelfpulse/terms" />
        <meta name="robots" content="noindex, follow" />
      </Helmet>

      {/* Navigation */}
      <nav className="sp-nav">
        <div className="sp-nav-container">
          <Link to="/shelfpulse" className="sp-nav-logo">
            <svg className="sp-logo-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M8 7h8M8 11h8M8 15h5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span className="sp-logo-text">ShelfPulse</span>
          </Link>
          <div className="sp-nav-links">
            <Link to="/shelfpulse" className="sp-nav-link">Home</Link>
            <Link to="/shelfpulse/privacy" className="sp-nav-link">Privacy Policy</Link>
          </div>
        </div>
      </nav>

      <div className="sp-legal-container">
        <Link to="/shelfpulse" className="sp-back-link">← Back to Home</Link>

        <div className="sp-legal-header">
          <h1 className="sp-legal-title">Terms of Service</h1>
          <p className="sp-legal-updated">Last Updated: October 23, 2026</p>
        </div>

        <div className="sp-legal-content">
          <p>Welcome to ShelfPulse! These Terms of Service ("Terms") govern your use of the ShelfPulse app for iPhone, iPad, and Android ("the App"). By downloading or using the App, you agree to these Terms. If you do not agree, please do not use the App.</p>

          <h2>1. What ShelfPulse Does</h2>
          <p>ShelfPulse is a companion app for Audiobookshelf servers. Depending on your account on that server, it lets you:</p>
          <ul>
            <li>See live listening sessions and listening history</li>
            <li>View user, library, and device statistics</li>
            <li>Edit books, covers, chapters, authors, series, genres, tags, and narrators</li>
            <li>Manage users, libraries, collections, API keys, backups, and server maintenance</li>
            <li>Use home screen widgets and choose from several themes</li>
          </ul>
          <p>ShelfPulse is an independent app. It is not made by, or affiliated with, the Audiobookshelf project.</p>

          <h2>2. License</h2>
          <p>As long as you follow these Terms, we grant you a limited, non-exclusive, non-transferable, revocable license to install and use the App on iOS, iPadOS, and Android devices you own or control, for your personal use and to manage servers you are authorized to manage.</p>

          <h2>3. Your Server and Your Account</h2>
          <ul>
            <li>You need access to an Audiobookshelf server and an account on it.</li>
            <li>Only connect to servers you are authorized to use, and only manage servers you are authorized to manage.</li>
            <li>You are responsible for keeping your sign-in details safe and for everything done with your account.</li>
          </ul>

          <h2>4. Changes You Make to Your Server</h2>
          <p>Many features change data on your server directly, including editing or deleting books, merging authors or series, renaming genres and narrators, converting audio files, deleting files, changing users and passwords, editing or deleting libraries, and managing API keys and backups. Some of these changes cannot be undone.</p>
          <p>The App asks you to confirm destructive actions, but you are responsible for the changes you make. We strongly recommend keeping regular Audiobookshelf backups. ShelfPulse is not responsible for data lost or changed as a result of actions you take in the App.</p>

          <h2>5. Temporary Passwords</h2>
          <p>Administrators can give a user a temporary password from ShelfPulse. Our Audra app asks anyone who signs in with one of these passwords to choose a new password. Other Audiobookshelf apps do not, so a temporary password stays in effect there until it is changed. Temporary passwords come from a fixed list and are meant to be used only briefly.</p>

          <h2>6. Purchases and Subscriptions</h2>
          <p>Some features may require a one-time purchase or a subscription, which may include a free trial.</p>
          <ul>
            <li>Purchases are processed by Apple (App Store) or Google (Google Play) and charged to the account you use with that store.</li>
            <li>Subscriptions renew automatically unless you cancel at least 24 hours before the end of the current period. Your account is charged for renewal within 24 hours before the period ends.</li>
            <li>If you cancel during a free trial, you will not be charged. Any unused part of a free trial ends when you buy a subscription.</li>
            <li>You can manage or cancel subscriptions in your App Store account settings or in Google Play (Payments &amp; subscriptions).</li>
            <li>Refunds are handled by Apple or Google under their refund policies.</li>
          </ul>

          <h2>7. Acceptable Use</h2>
          <p>You agree not to:</p>
          <ul>
            <li>Use the App to access or change servers or accounts without authorization</li>
            <li>Reverse engineer, decompile, or disassemble the App, except where the law allows it</li>
            <li>Use the App to break any law or anyone else's rights</li>
            <li>Interfere with or disrupt the App or any server it connects to</li>
          </ul>

          <h2>8. Intellectual Property</h2>
          <p>ShelfPulse, its name, logo, design, and content belong to Eric Randall and are protected by copyright, trademark, and other laws. Audiobookshelf is a separate open-source project owned by its contributors. You may not copy, modify, distribute, sell, or lease any part of the App without written permission.</p>

          <h2>9. Third-Party Services</h2>
          <p>The App relies on your Audiobookshelf server and on services described in our Privacy Policy, such as Apple, Google, ip-api.com, and GitHub. We are not responsible for their availability, content, security, or policies, and your use of them is subject to their own terms.</p>

          <h2>10. Privacy</h2>
          <p>Our <Link to="/shelfpulse/privacy">Privacy Policy</Link> explains how the App handles information. It is part of these Terms.</p>

          <h2>11. Disclaimer of Warranties</h2>
          <p>THE APP IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE APP WILL BE UNINTERRUPTED, SECURE, OR ERROR-FREE, THAT STATISTICS SHOWN WILL BE ACCURATE, OR THAT THE APP WILL WORK WITH EVERY DEVICE, OPERATING SYSTEM, OR AUDIOBOOKSHELF VERSION.</p>

          <h2>12. Limitation of Liability</h2>
          <p>TO THE FULLEST EXTENT PERMITTED BY LAW, WE ARE NOT LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR FOR ANY LOSS OF DATA, PROFITS, OR GOODWILL, ARISING FROM YOUR USE OF OR INABILITY TO USE THE APP, INCLUDING ANY CHANGES MADE TO YOUR SERVER THROUGH THE APP. OUR TOTAL LIABILITY WILL NOT EXCEED THE AMOUNT YOU PAID FOR THE APP IN THE TWELVE (12) MONTHS BEFORE THE CLAIM.</p>

          <h2>13. Indemnification</h2>
          <p>You agree to indemnify and hold harmless ShelfPulse and its developer from claims, damages, and expenses, including reasonable attorneys' fees, arising from your use of the App, your management of any server, or your breach of these Terms.</p>

          <h2>14. Changes and Termination</h2>
          <p>We may update these Terms. We will post changes on this page and update the date at the top; continuing to use the App means you accept them. We may suspend or end your access to the App if you break these Terms. You can stop using the App at any time by deleting it.</p>

          <h2>15. App Store Terms</h2>
          <p><strong>Apple:</strong> these Terms are between you and us, not Apple. Apple has no obligation to provide maintenance or support for the App and is not responsible for claims relating to it. If the App fails to conform to any applicable warranty, you may notify Apple for a refund of the purchase price, if any. Apple and its subsidiaries are third-party beneficiaries of these Terms and may enforce them. Your use of the App must also follow the App Store Terms of Service.</p>
          <p><strong>Google:</strong> if you got the App from Google Play, your use must also follow the Google Play Terms of Service. Google is not a party to these Terms and is not responsible for the App.</p>

          <h2>16. Governing Law</h2>
          <p>These Terms are governed by the laws of the State of Utah, United States, without regard to conflict-of-law rules. Disputes will be resolved exclusively in the courts of Cache County, Utah.</p>

          <h2>17. Severability and Entire Agreement</h2>
          <p>If any part of these Terms is found unenforceable, the rest stays in effect. These Terms and the Privacy Policy are the entire agreement between you and us about the App.</p>

          <h2>18. Contact</h2>
          <p>
            <strong>Eric Randall</strong><br />
            Email: hello@audrabooks.app<br />
            Address: 25 S 200 W, Hyde Park, UT 84318<br />
            United States
          </p>
        </div>
      </div>

      {/* Footer */}
      <footer className="sp-footer">
        <div className="sp-footer-container">
          <div className="sp-footer-content">
            <div className="sp-footer-brand">
              <div className="sp-footer-logo">
                <svg className="sp-logo-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span className="sp-logo-text">ShelfPulse</span>
              </div>
              <p className="sp-footer-tagline">Your Audiobook Dashboard</p>
            </div>
          </div>
          <div className="sp-footer-bottom">
            <p>&copy; {new Date().getFullYear()} ShelfPulse. All rights reserved.</p>
            <div className="sp-footer-links">
              <Link to="/shelfpulse/terms">Terms of Service</Link>
              <Link to="/shelfpulse/privacy">Privacy Policy</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default ShelfPulseTerms;
